import React, { useState } from 'react';
import { OverlayTrigger, Tooltip as BSTooltip } from 'react-bootstrap';

// Centralized glossary of terms
const glossary = {
  // Command Center Tools
  'profit zone': 'Your strategy library featuring battle-tested tactics from marketing legends like Dan Kennedy, Jay Abraham, and more.',
  'profit advisor': 'Your AI business consultant that knows your business, customers, and products. Ask it anything about strategy, implementation, or troubleshooting.',
  'campaign hq': 'The digital marketing heart of the Command Center. Run attraction marketing, paid ads, influencer campaigns, and AI-powered cold calling—all from one place.',
  'ai sop assistant': 'Helps you document and systematize your business operations so it can run without you. Turn the knowledge in your head into clear, delegatable processes.',
  'zeitgeist': 'Your real-time pulse on culture and competition. Monitors trending topics, memes, and language patterns to keep your content fresh. Also tracks competitor launches and identifies market gaps for your next big move.',
  'command center': 'The Command Center — your upgraded AI-powered platform where your Business Strategist, Campaign HQ, Zeitgeist, and AI team work together to grow your business.',
  'profit command center': 'Your upgraded AI-powered platform where your Business Strategist, Campaign HQ, Zeitgeist, and AI team work together to grow your business.',

  // Industry Jargon - Metrics
  'cac': 'Customer Acquisition Cost — the total cost to acquire one new customer (marketing + sales expenses ÷ new customers).',
  'cac payback': 'How many months until a customer\'s revenue covers what you spent to acquire them. Under 6 months is ideal.',
  'cac payback period': 'How many months until a customer\'s revenue covers what you spent to acquire them. Under 6 months is ideal.',
  'recurring revenue': 'Predictable income from subscriptions or repeat purchases, as opposed to one-time sales. Highly valued by acquirers.',
  'customer retention': 'The percentage of customers who continue paying you over time. 90%+ annually is excellent.',
  'customer retention rate': 'The percentage of customers who continue paying you over time. 90%+ annually is excellent.',
  'retention rate': 'The percentage of customers who continue paying you over time. 90%+ annually is excellent.',
  'churn': 'The percentage of customers who stop paying you. The opposite of retention. Lower is better.',
  'churn rate': 'The percentage of customers who stop paying you. The opposite of retention. Lower is better.',
  'high churn rate': 'Too many customers leaving. This kills business value because you\'re constantly replacing lost revenue instead of growing.',
  'ltv': 'Lifetime Value — the total revenue you\'ll earn from a customer over your entire relationship with them.',
  'ltv:cac': 'Lifetime Value to Customer Acquisition Cost ratio. 3:1 or higher means you earn $3+ for every $1 spent acquiring customers.',
  'ltv:cac ratio': 'Lifetime Value to Customer Acquisition Cost ratio. 3:1 or higher means you earn $3+ for every $1 spent acquiring customers.',
  'yoy': 'Year over Year — comparing this year\'s performance to the same period last year.',
  'competitive moat': 'What makes your business hard to copy or compete with. Could be technology, brand, network effects, or switching costs.',
  'product-market fit': 'When your product truly solves a real problem that people will pay for. The foundation of sustainable growth.',
  'unit economics': 'The revenue and costs associated with a single customer. If you lose money per customer, scaling just loses money faster.',
  'technical debt': 'Shortcuts taken in code or systems that work now but will need fixing later. Slows down future development.',
  'owner dependency': 'When the business can\'t function without the owner. A major red flag for acquirers who want a business, not a job.',
  'customer concentration': 'When too much revenue comes from too few customers. If one big client leaves, the business is in trouble.',
  'customer concentration risk': 'When too much revenue comes from too few customers. If one big client leaves, the business is in trouble.',
  'single channel dependency': 'Relying on just one way to get customers (e.g., only Facebook ads). If that channel dies, so does your business.',
  'burn rate': 'How fast you\'re spending money. High burn without a path to profitability is a red flag.',
  'scalability': 'Can the business grow 10x without breaking? Or do costs and complexity grow just as fast as revenue?',

  // Other terms
  'roi': 'Return on Investment — how much profit you make compared to what you spent.',
  'kpi': 'Key Performance Indicator — the most important metrics that show if your business is healthy.',
  'kpis': 'Key Performance Indicators — the most important metrics that show if your business is healthy.',

  // Funnel & Marketing terms
  'value ladder': 'Your sequence of products at increasing price points. Customers enter at a low price and upgrade to higher-value offers over time.',
  'lead magnet': 'A free resource (guide, checklist, video) you offer in exchange for someone\'s email address. The first step in your value ladder.',
  'tripwire': 'A low-priced offer ($7-$47) that converts a free subscriber into a paying customer. Builds buying momentum.',
  'organic marketing': 'Marketing that doesn\'t require paid advertising — content, SEO, social media, referrals. Takes longer but builds lasting assets.',
  'organic acquisition': 'Getting customers without paying for ads. Includes content marketing, SEO, social media, and referrals.',
  'jv partnership': 'Joint Venture — partnering with another business to cross-promote to each other\'s audiences.',
  'affiliate program': 'Paying others a commission to refer customers to you. They promote, you pay only for results.',
  'strategic alliance': 'A formal partnership where two businesses work together for mutual benefit, beyond just referrals.',
  'content calendar': 'A schedule of what content to publish, when, and on which platforms. Keeps your marketing consistent.',
  'posting schedule': 'When and how often you publish content on each social media platform.',
  'attraction marketing': 'Creating valuable content that naturally draws potential customers to you, rather than interrupting them with ads.',

  // Customer Journey & Persona terms
  'persona': 'A detailed profile of your ideal customer type, including their goals, challenges, and buying behavior. Based on real customer data.',
  'customer persona': 'A detailed profile of your ideal customer type, including their goals, challenges, and buying behavior.',
  'primary persona': 'Your main target customer to focus marketing efforts on first. Start here, then expand to other personas.',
  'target market': 'The specific group of potential customers you\'re trying to reach with your product or service.',
  'niche': 'A specialized, focused segment of the market. Narrowing your niche makes marketing easier and more effective.',
  'customer journey': 'The complete path a customer takes from first discovering you to becoming a loyal buyer and advocate.',
  'journey map': 'A visual representation of every touchpoint and emotion your customer experiences from awareness to purchase.',
  'pain point': 'A specific problem, frustration, or challenge your customers experience that your product solves.',
  'pain intensity': 'How urgently the customer feels their problem. Higher pain = more motivated to buy a solution.',
  'decision authority': 'Whether a persona has the power and budget to make purchasing decisions, or needs approval from others.',
  'market size': 'The total number of potential customers in a segment. Bigger isn\'t always better — focus matters.',

  // Customer Journey Stages
  'problem aware': 'Stage 1: Customer knows they have a problem but hasn\'t started looking for solutions yet.',
  'solution aware': 'Stage 2: Customer is researching solutions and comparing options. They know help exists.',
  'decision ready': 'Stage 3: Customer is evaluating specific products/services and ready to make a purchase decision.',
  'customer success': 'Stage 4: Post-purchase focus on helping customers achieve results, leading to retention and referrals.',

  // Business Foundation terms
  'core values': 'The fundamental beliefs and principles that guide your business decisions and culture. They define who you are.',
  'mission statement': 'A concise statement of WHY your business exists and WHO you serve. Your purpose and impact.',
  'vision statement': 'WHERE your business is headed. The future state you\'re working toward. Inspires and guides long-term direction.',
  'capacity assessment': 'Understanding how many customers you can serve well right now, and what it takes to scale up.',
  'scaling constraints': 'The bottlenecks that limit how fast you can grow — could be time, team, systems, or capital.',
  'results & outcomes': 'The specific, measurable improvements your customers achieve by working with you.',

  // Exit Strategy terms
  'exit strategy': 'Your long-term plan for the business — whether to sell it, pass it on, or build for lifestyle income.',
  'exit path': 'The specific route you\'ll take: lifestyle business, build-to-sell, strategic acquisition, or legacy/family business.',
  'lifestyle business': 'A business designed for sustainable income and freedom, not necessarily for sale.',
  'build to sell': 'Building a business with the specific goal of selling it to another company or investor.',
  'strategic acquisition': 'Positioning your business to be acquired by a larger company in your industry.',
};

// Function to find and wrap terms with tooltips
export const addTooltips = (text) => {
  if (!text || typeof text !== 'string') return text;

  let result = text;
  const sortedTerms = Object.keys(glossary).sort((a, b) => b.length - a.length);

  sortedTerms.forEach(term => {
    const regex = new RegExp(`\\b(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi');
    result = result.replace(regex, `[[TOOLTIP:$1]]`);
  });

  return result;
};

// Tooltip wrapper component
const TooltipTerm = ({ term, children }) => {
  const definition = glossary[term.toLowerCase()];

  if (!definition) {
    return <span>{children}</span>;
  }

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <BSTooltip id={`tooltip-${term}`} style={{ maxWidth: '300px' }}>
          {definition}
        </BSTooltip>
      }
    >
      <span style={{
        borderBottom: '1px dotted #6f42c1',
        cursor: 'help',
        color: 'inherit'
      }}>
        {children}
      </span>
    </OverlayTrigger>
  );
};

// Helper component to render text with tooltips
export const TextWithTooltips = ({ text }) => {
  if (!text) return null;

  // Sort terms by length (longest first) to avoid partial matches
  const sortedTerms = Object.keys(glossary).sort((a, b) => b.length - a.length);

  // Create a regex pattern that matches any glossary term
  const pattern = sortedTerms
    .map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');

  const regex = new RegExp(`\\b(${pattern})\\b`, 'gi');

  // Split text by matches and rebuild with tooltip components
  const parts = [];
  let lastIndex = 0;
  let match;

  // Reset regex state
  regex.lastIndex = 0;

  const textStr = String(text);

  while ((match = regex.exec(textStr)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(textStr.slice(lastIndex, match.index));
    }

    // Add matched term with tooltip
    parts.push(
      <TooltipTerm key={match.index} term={match[0]}>
        {match[0]}
      </TooltipTerm>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < textStr.length) {
    parts.push(textStr.slice(lastIndex));
  }

  return <>{parts}</>;
};

export default TooltipTerm;
